import { ClickAwayListener, Input, List, Paper, Typography } from '@mui/material';
import { StyledComponentProps } from "@mui/styles";
import * as coreApi from "api/Core";
import { GlobalSearchClassKey } from 'assets/jss/common-styles';
import { HeaderStylesClassKey } from 'assets/jss/layouts/headerLinksStyle';
import classNames from 'classnames';
import IconButtonCancel from 'components/Buttons/IconButtonCancel';
import { SpinnerIndicator } from 'components/CallServer';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiPaper from 'components/MuiExtras/MuiPaper';
import AppResource from 'components/Translations/AppResources';
import { AccessKeys } from 'Constants';
import { GlobalSearchResult } from 'models/ApiModels';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router';
import { RoutePaths } from 'routes/RouteConfig';
import { SearchRenderCourse } from 'views/Search/Components/SearchRenderCourse';
import { SearchRenderDocument } from './SearchRenderDocument';
import { SearchRenderPublication } from './SearchRenderPublication';
import { SearchRenderTopic } from './SearchRenderTopic';
import { SearchRenderUser } from './SearchRenderUser';

//////////////////////////////////////////////////////////////////////////////////

interface IProps {
}

interface IState {
    focused: boolean;
    opened: boolean;
    loading: boolean;
    searchResult: GlobalSearchResult;
    searchText: string;
    inputText: string;
}

type Props = IProps & WithTranslation & RouteComponentProps<{}> & StyledComponentProps<HeaderStylesClassKey> & StyledComponentProps<GlobalSearchClassKey>;

const MinChars = 2;

//////////////////////////////////////////////////////////////////////////////////

class GlobalSearchPreview extends React.PureComponent<Props, IState> {
    private searchRef: any;
    private lastChange: number;
    private request = 1;
    private timer: NodeJS.Timeout;

    constructor(props: Props) {
        super(props);
        this.state = {
            focused: false,
            opened: false,
            loading: false,
            searchText: "",
            inputText: "",
            searchResult: null
        };
    }

    ///
    private handleMouseEnter = () => {
        this.setState({
            focused: true,
            opened: true
        }, this.searchTextChange);
    };

    ///
    private handleMouseOut = () => {
        this.setState({
            focused: false,
            opened: this.calcTextChars(this.state.searchText) >= MinChars
        });
    };

    ///
    private handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const { searchText } = this.state;
        if (event.keyCode == 13 && this.calcTextChars(searchText) >= MinChars) {
            this.redirect();
        }
    };

    ///
    private handleClose = () => {
        if (!this.state.focused) {
            this.reset();
        };
    };

    ///
    private redirect() {
        const { inputText, searchText } = this.state;

        var firstChar = inputText[0];
        if (firstChar == "@")
            this.props.history.replace(RoutePaths.FullUserSearch + "?q=" + encodeURIComponent(searchText));
        else if (firstChar == "!")
            this.props.history.replace(RoutePaths.FullUserSearch + "?q=" + encodeURIComponent(searchText) + "&sp=1");
        else if (firstChar == "#")
            this.props.history.replace(RoutePaths.FullTopicSearch + "?q=" + encodeURIComponent(searchText));
        else if (firstChar == "$")
            this.props.history.replace(RoutePaths.FullCourseSearch + "?q=" + encodeURIComponent(searchText));
        else
            this.props.history.replace(RoutePaths.FullGlobalSearch + "?q=" + encodeURIComponent(inputText));

        this.reset();
    }

    ///
    private calcTextChars(inputText: string) {
        var len = inputText.length;
        var firstChar = inputText[0];
        if (firstChar == "@" || firstChar == "!" || firstChar == "#" || firstChar == "$") len -= 1;
        return len;
    }

    ///
    private reset() {
        this.setState({
            focused: false,
            opened: false,
            loading: false,
            searchText: "",
            searchResult: null
        });
    }

    ///
    private resetText = () => {
        this.setState({
            focused: false,
            opened: false,
            loading: false,
            inputText: "",
            searchText: "",
            searchResult: null
        });
    };

    ///
    private getSearchText(text: string) {
        var firstChar = text[0];
        if (firstChar == "@" || firstChar == "@" || firstChar == "!" || firstChar == "$" || firstChar == "&")
            return text.substr(1);
        return text;
    }

    ///
    private searchTextChange = () => {
        if (this.timer) clearTimeout(this.timer);

        var searchText = this.getSearchText(this.searchRef.value);

        this.setState({
            inputText: this.searchRef.value,
            searchText: searchText,
            opened: this.searchRef.value.length > 0
        });

        // Manipula sugestões após parar de teclar por 1s.
        this.lastChange = new Date().getTime();
        this.timer = setTimeout(() => {
            if ((new Date().getTime() - this.lastChange) > 990) {
                this.checkSuggestion();
            }
        }, 1000);
    };

    ///
    private renderInfo() {
        return (
            <>
                <Typography variant="button">
                    <AppResource resource="Views.Search.Components.GlobalSearchPreview.TipTitle" />
                </Typography>
                <Typography variant="body1" >
                    <AppResource resource="Views.Search.Components.GlobalSearchPreview.TypeIn" />:
                    <br />
                    <code>
                        <AppResource resource="Views.Search.Components.GlobalSearchPreview.TipPeople" />
                        <br />
                        <AppResource resource="Views.Search.Components.GlobalSearchPreview.TipTopic" />
                        <br />
                        <AppResource resource="Views.Search.Components.GlobalSearchPreview.TipSpecialist" />
                        <br />
                        <AppResource resource="Views.Search.Components.GlobalSearchPreview.TipCourse" />
                    </code>
                </Typography>
            </>
        );
    }

    ///
    private checkSuggestion() {
        if (this.calcTextChars(this.state.searchText) < MinChars)
            return;

        const curRequest = ++this.request;

        this.setState({ loading: true, searchResult: null });

        coreApi.globalSearchPreview(this.state.inputText)
            .then((result) => {
                if (curRequest == this.request) {
                    this.setState({ loading: false, searchResult: result });
                }
            });
    }

    ///
    private renderPreview() {

        const { searchResult, searchText } = this.state;

        if (searchResult == null)
            return null;

        if (searchResult.publications.totalRecords == 0 &&
            searchResult.topics.totalRecords == 0 &&
            searchResult.courses.totalRecords == 0 &&
            searchResult.documents.totalRecords == 0 &&
            searchResult.users.totalRecords == 0)
            return (
                <MuiContainer margin="dense">
                    <Typography variant="button">
                        <b><AppResource resource="Common.Filter.ThereAreNoResults" /></b>
                    </Typography>
                </MuiContainer>
            );

        return (
            <div style={{ maxHeight: 250, overflow: "auto" }}>
                <MuiContainer margin="dense">
                    <Typography variant="button">
                        <AppResource resource="Common.Filter.SomeResults" />
                    </Typography>
                </MuiContainer>

                {
                    searchResult.users.items.length > 0 &&
                    <MuiPaper disablePadding disableGutters paperColor="white" borderVariant="square" borderColor="transparent" className={this.props.classes.userCard}>
                        <List dense>
                            {
                                searchResult.users.items.map((v, index) => {
                                    return <SearchRenderUser key={v.id} data={v} index={index} showDivider={false} />;
                                })
                            }
                        </List>
                    </MuiPaper>
                }

                {
                    searchResult.topics.items.length > 0 &&
                    <MuiPaper disablePadding disableGutters paperColor="white" borderVariant="square" borderColor="transparent" className={this.props.classes.topicCard}>
                        <List dense>
                            {
                                searchResult.topics.items.map((v, index) => {
                                    return <SearchRenderTopic key={v.id} data={v} index={index} showDivider={false} />;
                                })
                            }
                        </List>
                    </MuiPaper>
                }

                {
                    searchResult.courses.items.length > 0 &&
                    <MuiPaper disablePadding disableGutters paperColor="white" borderVariant="square" borderColor="transparent" className={this.props.classes.courseCard}>
                        <List dense>
                            {
                                searchResult.courses.items.map((v, index) => {
                                    return <SearchRenderCourse key={v.id} data={v} index={index} showDivider={false} {...this.props} />;
                                })
                            }
                        </List>
                    </MuiPaper>
                }

                {
                    searchResult.publications.items.length > 0 &&
                    <MuiPaper disablePadding disableGutters paperColor="white" borderVariant="square" borderColor="transparent" className={this.props.classes.publicationCard}>
                        <List dense>
                            {
                                searchResult.publications.items.map((v, index) => {
                                    return <SearchRenderPublication key={v.id} data={v} index={index} showDivider={false} querySearch={searchText} />;
                                })
                            }
                        </List>
                    </MuiPaper>
                }

                {
                    searchResult.documents.items.length > 0 &&
                    <MuiPaper disablePadding disableGutters paperColor="white" borderVariant="square" borderColor="transparent" className={this.props.classes.documentCard}>
                        <List dense>
                            {
                                searchResult.documents.items.map((v, index) => {
                                    return <SearchRenderDocument key={v.id} data={v} index={index} showDivider={false} querySearch={searchText} />;
                                })
                            }
                        </List>
                    </MuiPaper>
                }
            </div>
        );
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.location.pathname != prevProps.location.pathname && this.props.location.pathname.indexOf("search") == -1) {
            this.resetText();
        }
    }

    ///
    render() {
        const { classes } = this.props;

        return (
            <div
                className={
                    classNames({
                        [classes.searchOuter]: true,
                        ["searching"]: this.state.focused
                    })
                }
            >
                <div className={classes.searchInner} >
                    <div className={classes.searchInner} >
                        <Input
                            id="full-width"
                            placeholder={this.props.t("Views.Search.Components.GlobalSearchPreview.Input.Placeholder")}
                            className={classes.searchInput}
                            disableUnderline
                            onFocus={this.handleMouseEnter}
                            onBlur={this.handleMouseOut}
                            onChange={this.searchTextChange}
                            onKeyDown={this.handleKeyDown}
                            value={this.state.inputText}
                            autoComplete="off"
                            spellCheck={false}
                            inputRef={(ref) => this.searchRef = ref}
                            endAdornment={
                                this.state.inputText == ""
                                    ? null
                                    : <IconButtonCancel color="inherit" size="small" onClick={this.resetText} />
                            }
                            accessKey={AccessKeys.Search}
                        />
                    </div>

                    <ClickAwayListener onClickAway={this.handleClose}>
                        <Paper square className={classes.searchInfo} style={{ display: (this.state.opened ? "" : "none") }} >
                            {
                                this.state.loading
                                    ? <SpinnerIndicator />
                                    : this.state.searchText.length < MinChars
                                        ? this.renderInfo()
                                        : this.renderPreview()
                            }
                        </Paper>
                    </ClickAwayListener>
                </div>
            </div>
        );
    }
}

export default withTranslation()(withRouter(GlobalSearchPreview));